import { useEffect, useState } from 'react';
import useFetch from '../hooks/api';
import { KeyRound, ShieldCheck, Smartphone } from 'lucide-react';
import { Button } from '../shadcn/Button';
import Input from './Input';
import Notice from './Notice';
import { QRCodeSVG } from 'qrcode.react';
import { cn } from '../shadcn/utils';

type TOTPState = 'loading' | 'setup' | 'scan_qr' | 'confirm' | 'created' | 'verify' | 'success' | 'locked';

interface TOTPAuthProps {
	/** Whether the user has already set up TOTP (totp_state === 'active') */
	isSetUp: boolean;
	/** Called when TOTP verification succeeds, returns the 6-digit code used */
	onSuccess: (code: string) => void;
	/** If false, skips calling /verify and passes the code directly to onSuccess (for endpoints that verify internally). Defaults to true. */
	callVerifyEndpoint?: boolean;
	className?: string;
}

export default function TOTPAuth({ isSetUp, onSuccess, callVerifyEndpoint = true, className }: TOTPAuthProps) {
	const [step, setStep] = useState<TOTPState>(isSetUp ? 'verify' : 'setup');
	const [code, setCode] = useState('');
	const [errorMessage, setErrorMessage] = useState('');

	// Provision endpoint
	const [doProvision, setDoProvision] = useState(false);
	const {
		data: provisionResponse,
		error: provisionError,
		loading: provisionLoading,
		reset: resetProvision,
		refetch: refetchProvision,
	} = useFetch('https://api.krece.app/notifications/2fa/provision/', 'POST', doProvision, undefined, undefined, true);

	// Confirm endpoint (first-time setup verification)
	const [doConfirm, setDoConfirm] = useState(false);
	const {
		data: confirmResponse,
		setPayload: setConfirmPayload,
		error: confirmError,
		loading: confirmLoading,
	} = useFetch('https://api.krece.app/notifications/2fa/confirm/', 'POST', doConfirm, undefined, undefined, true);

	// Verify endpoint (ongoing verification)
	const [doVerify, setDoVerify] = useState(false);
	const {
		data: verifyResponse,
		setPayload: setVerifyPayload,
		error: verifyError,
		loading: verifyLoading,
	} = useFetch('https://api.krece.app/notifications/2fa/verify/', 'POST', doVerify, undefined, undefined, true);

	// Handle provision response
	useEffect(() => {
		if (provisionResponse?.success && provisionResponse.provisioning_uri) {
			setStep('scan_qr');
		}
	}, [provisionResponse]);

	useEffect(() => {
		if (provisionError) {
			setErrorMessage(typeof provisionError === 'string' ? provisionError : 'Error generando el código QR. Intente nuevamente.');
		}
	}, [provisionError]);

	// Handle confirm response — creation only, do NOT call onSuccess
	useEffect(() => {
		if (confirmResponse?.success) {
			setStep('created');
			setCode('');
			setErrorMessage('');
		}
	}, [confirmResponse]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (confirmError) {
			const msg = typeof confirmError === 'string' ? confirmError : 'Código inválido. Intente nuevamente.';
			if (msg.toLowerCase().includes('lock')) {
				setStep('locked');
				setErrorMessage('Cuenta bloqueada por demasiados intentos fallidos. Contacte soporte.');
			} else {
				setErrorMessage(msg);
			}
			setCode('');
		}
	}, [confirmError]);

	// Handle verify response
	useEffect(() => {
		if (verifyResponse?.success) {
			setStep('success');
			onSuccess(code);
		}
	}, [verifyResponse]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (verifyError) {
			const msg = typeof verifyError === 'string' ? verifyError : 'Código inválido. Intente nuevamente.';
			if (msg.toLowerCase().includes('lock')) {
				setStep('locked');
				setErrorMessage('Cuenta bloqueada por demasiados intentos fallidos. Contacte soporte.');
			} else {
				setErrorMessage(msg);
			}
			setCode('');
		}
	}, [verifyError]);

	// Auto-submit when 6 digits entered
	useEffect(() => {
		if (code.length === 6) {
			setErrorMessage('');
			if (step === 'confirm') {
				setDoConfirm(true);
				setConfirmPayload({ code });
			} else if (step === 'verify') {
				if (callVerifyEndpoint) {
					setDoVerify(true);
					setVerifyPayload({ code });
				} else {
					setStep('success');
					onSuccess(code);
				}
			}
		}
	}, [code]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleStartSetup = () => {
		setErrorMessage('');
		if (doProvision) {
			resetProvision();
			refetchProvision();
		} else {
			setDoProvision(true);
		}
	};

	const handleQRScanned = () => {
		setStep('confirm');
		setCode('');
		setErrorMessage('');
	};

	return (
		<div className={cn('flex flex-col gap-3', className)}>
			{/* Setup step: user needs to provision authenticator */}
			{step === 'setup' && (
				<>
					<div className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-background px-3 py-2.5 dark:border-neutral-800">
						<Smartphone className="size-5 shrink-0 text-violet-500" />
						<div className="flex flex-col">
							<span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Autenticador TOTP</span>
							<span className="text-xs text-neutral-500 dark:text-neutral-400">
								Configura tu aplicación de autenticación (Google Authenticator, Authy, etc.)
							</span>
						</div>
					</div>
					<Button
						variant="primary"
						loading={provisionLoading}
						disabled={provisionLoading}
						className="w-full bg-violet-600 hover:bg-violet-700"
						onClick={handleStartSetup}
					>
						<div className="flex w-full items-center justify-center gap-2">
							<ShieldCheck className="size-4" />
							<span>Configurar autenticador</span>
						</div>
					</Button>
				</>
			)}

			{/* QR code step */}
			{step === 'scan_qr' && provisionResponse?.provisioning_uri && (
				<>
					<p className="text-sm text-neutral-600 dark:text-neutral-400">
						Escanea este código QR con tu aplicación de autenticación:
					</p>
					<div className="flex justify-center rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-800">
						<QRCodeSVG value={provisionResponse.provisioning_uri} size={200} />
					</div>
					<p className="text-xs text-neutral-500 dark:text-neutral-400">
						Una vez escaneado, presiona continuar para verificar la configuración.
					</p>
					<Button variant="primary" className="w-full bg-violet-600 hover:bg-violet-700" onClick={handleQRScanned}>
						<div className="flex w-full items-center justify-center gap-2">
							<KeyRound className="size-4" />
							<span>Continuar</span>
						</div>
					</Button>
				</>
			)}

			{/* Confirm step (first-time setup) */}
			{step === 'confirm' && (
				<>
					<p className="text-sm text-neutral-600 dark:text-neutral-400">
						Ingresa el código de 6 dígitos de tu aplicación de autenticación para confirmar la configuración:
					</p>
					<Input
						disabled={confirmLoading}
						containerClassName="w-full"
						Icon={<KeyRound className="size-4 text-violet-500" />}
						value={code}
						onChange={(value) => setCode(value)}
						placeholder="Ingresa el código de 6 dígitos"
						allowedChars="numbers_only"
						floatingLabel="Código TOTP"
						maxLength={6}
					/>
					{confirmLoading && (
						<div className="flex items-center gap-2 text-sm text-neutral-400">
							<div className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-600 border-t-violet-500" />
							Verificando...
						</div>
					)}
				</>
			)}

			{/* Created step — authenticator set up successfully, now must verify */}
			{step === 'created' && (
				<>
					<Notice message="Cuenta de OTP Creada" variant="success" />
					<p className="text-sm text-neutral-600 dark:text-neutral-400">
						Tu autenticador fue configurado correctamente. Ahora ingresa un nuevo código para confirmar la acción.
					</p>
					<Button
						variant="primary"
						className="w-full bg-violet-600 hover:bg-violet-700"
						onClick={() => {
							setStep('verify');
							setCode('');
							setErrorMessage('');
						}}
					>
						<div className="flex w-full items-center justify-center gap-2">
							<KeyRound className="size-4" />
							<span>Continuar con verificación</span>
						</div>
					</Button>
				</>
			)}

			{/* Verify step (already set up) */}
			{step === 'verify' && (
				<>
					<div className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-background px-3 py-2.5 dark:border-neutral-800">
						<ShieldCheck className="size-5 shrink-0 text-violet-500" />
						<div className="flex flex-col">
							<span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Verificación TOTP</span>
							<span className="text-xs text-neutral-500 dark:text-neutral-400">
								Ingresa el código de tu aplicación de autenticación
							</span>
						</div>
					</div>
					<Input
						disabled={verifyLoading}
						containerClassName="w-full"
						Icon={<KeyRound className="size-4 text-violet-500" />}
						value={code}
						onChange={(value) => setCode(value)}
						placeholder="Ingresa el código de 6 dígitos"
						allowedChars="numbers_only"
						floatingLabel="Código TOTP"
						maxLength={6}
					/>
					{verifyLoading && (
						<div className="flex items-center gap-2 text-sm text-neutral-400">
							<div className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-600 border-t-violet-500" />
							Verificando...
						</div>
					)}
					<button
						type="button"
						className="self-start text-xs text-violet-500 hover:text-violet-400 hover:underline"
						onClick={() => {
							setStep('setup');
							setCode('');
							setErrorMessage('');
						}}
					>
						No tengo autenticador configurado
					</button>
				</>
			)}

			{/* Locked state */}
			{step === 'locked' && (
				<Notice message="Cuenta bloqueada por demasiados intentos fallidos. Contacte soporte." variant="error" />
			)}

			{/* Error messages */}
			{errorMessage && step !== 'locked' && step !== 'success' && <Notice message={errorMessage} variant="error" />}

			{/* Success */}
			{step === 'success' && <Notice message="Verificación TOTP exitosa" variant="success" />}
		</div>
	);
}
